import {useForm} from 'antd/es/form/Form';
import {useCallback, useRef, useState, Dispatch, SetStateAction} from 'react';

import {IModalOpenerComponentRef} from 'components/modal-opener-component';
import {EErrorMessages} from 'shared/constants/messages';
import {ERequestType} from 'shared/types/requests';
import {performRequest, showMessageFromResponse} from 'shared/utils';
import {notify} from 'shared/utils/notifications';
import {useAppSelector} from 'store/config/hooks';
import {selectUserInfo} from 'store/slices/auth-slice';

import {API_URL, CONTEXT_URL} from '../api';
import {EOperationType, IDwhDataPreparationForm} from '../model/dwh-data-preparation-modal.types';

export const useDwhDataPreparationModal = () => {
    const [form] = useForm<IDwhDataPreparationForm>();
    const modalRef = useRef<IModalOpenerComponentRef>(null);
    const [operationType, setOperationType] = useState<EOperationType>(EOperationType.Protocol);
    const {userName} = useAppSelector(selectUserInfo) ?? {};

    const [isOpen, setIsOpen] = useState(false);

    const resetForm = useCallback(() => {
        form.resetFields();
        setOperationType(EOperationType.Protocol);
    }, [form, setOperationType]);

    const handleCloseModal = useCallback(
        (setIsModalOpen: Dispatch<SetStateAction<boolean>>) => {
            resetForm();
            setIsModalOpen(false);
        },
        [resetForm]
    );

    const handleFinish = useCallback(async () => {
        const fieldSetData = {
            [EOperationType.Protocol]: {userName},
            [EOperationType.Registry]: {userName},
            [EOperationType.Enrichment]: {
                taxCode: form.getFieldValue('taxCode'),
                kndCode: form.getFieldValue('kndCode'),
                reportingYear: form.getFieldValue('reportingYear'),
                taxPeriod: form.getFieldValue('taxPeriod'),
                corrNum: form.getFieldValue('corrNum'),
                userName,
            },
        };

        try {
            const response = await performRequest({
                url: API_URL[operationType],
                data: fieldSetData[operationType],
                method: ERequestType.POST,
                urlConfig: {contextUrl: CONTEXT_URL},
            });
            const isSuccess = response?.status === 200;

            if (isSuccess) {
                resetForm();
                modalRef.current?.setIsModalOpen(false);
            }

            showMessageFromResponse({
                response,
                isError: !isSuccess,
                customMessage: response?.data || EErrorMessages.QUERY_EXECUTION,
            });
        } catch (error) {
            notify({message: error?.response?.data || EErrorMessages.UNEXPECTED_ERROR_OCCURRED, isError: true});
        }
    }, [form, modalRef, operationType, userName]);

    return {modalRef, isOpen, form, setIsOpen, handleCloseModal, handleFinish, operationType, setOperationType};
};
